'use client';
import {FC, useEffect, useState} from 'react';
import styles from './BookingBlock.module.scss';
import {SingleEventInfo} from '@/models/EventModels';
import blizard from '../../app/assets/icons/blizard.svg';
import paused from '../../app/assets/icons/paused_light.svg';
import Image from 'next/image';
import BookingInfo from '../BookingInfo';
import BookingCounter from '../BookingCounter';
import AuthPopup from '../AuthPopup';
import {useParams, useRouter} from 'next/navigation';
import ButtonElement from '../ButtonElement';
import {mainPath} from '@/app/routes/MainPath';
import {useTranslations} from 'next-intl';
import classNames from "classnames";
import Alert from "@/shared/Alert/Alert";
import {useLocalStorage} from "usehooks-ts";
import {removeUserData} from "@/helpers/functions";

interface BookingBlockProps {
  className?: string;
  event?: SingleEventInfo;
}

enum EventStatus {
  Finished = 'finished',
  Active = 'active',
  Upcoming = 'upcoming',
  Paused = 'paused',
}

const BookingBlock: FC<BookingBlockProps> = ({className = '', event}) => {
  const [isOpenAuthForm, setIsOpenAuthForm] = useState<boolean>(false);
  const {slug, id} = useParams();
  const router = useRouter();
  const [token, setToken, removeToken] = useLocalStorage<string>('token', '');
  const [quantity, setQuantity] = useLocalStorage<number>('last_invoice_ticket_quantity', 1);
  const [authDate, setAuthDate] = useLocalStorage<number>('auth_date', 0);

  const startDate = new Date(event?.start || '');
  const endDate = new Date(event?.end || '');

  let status;
  if (endDate < new Date())
    status = EventStatus.Finished;

  else if (event?.status === 'paused')
    status = EventStatus.Paused;

  else if (startDate < new Date() && endDate > new Date())
    status = EventStatus.Active;

  else if (startDate > new Date())
    status = EventStatus.Upcoming;

  const canBuy = status === EventStatus.Active || status === EventStatus.Upcoming;

  useEffect(() => {
    setQuantity(1);
  }, []);

  const handleBuyTicket = async () => {
    const currentDate = new Date().getTime();
    const tokenExpired = authDate + 1000 * 60 * 60 * 24 * 7 < currentDate;  // 7 days
    if (!token || tokenExpired) {
      removeUserData();
      setIsOpenAuthForm(!isOpenAuthForm);
    } else {
      router.push(`${mainPath.buy_ticket.path}/${id}`);
    }
  };
  const price = event?.price || 1;
  const t = useTranslations('BOOKING_BLOCK');

  return (
    <div className={classNames(styles.root, className)}>
      <header className={styles.header}>
        <h2 className={styles.header__title}>{t('book')}</h2>
        {status !== EventStatus.Upcoming && <div className={styles.header__badge}>
          <span>
            {status === EventStatus.Paused && <Image src={paused} width="24" height="24" alt="paused"/>}
            {status === EventStatus.Active && <Image src={blizard} width="24" height="24" alt="blizard"/>}
            {status === EventStatus.Finished && <Image src={blizard} width="24" height="24" alt="blizard"/>}
          </span>
          <p>{t(`status.${status}`)}</p>
        </div>}
      </header>

      {(status === EventStatus.Paused || status === EventStatus.Finished) && <Alert
        type="default"
        message={t(`alerts.${status}.message`)}
        title={t(`alerts.${status}.title`)}
        className={styles.alert}
      />}

      <div className={styles.price__block}>
        <div className={styles.price}>
          <p>{t('price')}</p>
          <b>₴{event?.price}</b>
        </div>
        {canBuy && <BookingCounter
            className={styles.counter__mob}
            quantity={quantity}
            setQuantity={(q) => setQuantity(q)}
        />}
      </div>

      <BookingInfo event={event}/>
      {/*<p className={styles.description}>*/}
      {/*  Order and buy tickets for Kozak System. All stars. 10 років разом is possible online via the*/}
      {/*  Internet.*/}
      {/*</p>*/}
      {canBuy && <>
        <BookingCounter className={styles.counter} quantity={quantity} setQuantity={(q) => setQuantity(q)}/>
        <div className={styles.buy__container}>
          <ButtonElement
            className={styles.buy__button}
            onClick={() => handleBuyTicket()}
            type="button"
            text="buy">
            {t('buy')} - ₴{(+price * +quantity).toFixed(0)}
          </ButtonElement>
        </div>
      </>}

      <AuthPopup
        setIsOpen={() => setIsOpenAuthForm(false)}
        isOpen={isOpenAuthForm}
        onAuth={() => router.push(`${mainPath.buy_ticket.path}/${id}`)}
      />
    </div>
  );
};

export default BookingBlock;
